import { useMutation, useQuery } from 'react-query';
import { agentApi } from '../config/api';
import { QUERY_KEYS } from './constants';
import agentStore from '../store/agent/index';
import authStore from '../store/auth/index';
import { getRouteExtraContext, removeEmptyValues } from '../utils/routeContext';

const getMessageContext = extraContext => {
  const routeExtraContext = getRouteExtraContext();

  return {
    webpage_state: agentStore.currentScreen || agentStore.currentSubScreen,
    extra_context: removeEmptyValues({
      ...routeExtraContext,
      ...agentStore.extraContext,
      ...extraContext
    })
  };
};

const getMessagePayload = data => ({
  content: data.content,
  context: getMessageContext(data.context?.extra_context || data.extra_context || {})
});

const agentService = {
  getInfo: () => agentApi.get('info'),
  createChat: data => agentApi.post('api/chats/', data),
  getChats: (searchQuery, workflowId) => {
    const params = new URLSearchParams();
    if (searchQuery) params.append('search_text', searchQuery);
    if (workflowId) params.append('workflow_id', workflowId);
    const qs = params.toString();
    return agentApi.get(`api/chats/${qs ? `?${qs}` : ''}`);
  },
  getMessagesByChatId: chatId => agentApi.get(`api/messages/${chatId}`),
  sendMessageToChat: ({ chatId, data }) => agentApi.post(`api/messages/${chatId}`, data),
  explainReportReviewFinding: data => agentApi.post('api/report-review/explain', data),
  summarizeReportReview: data => agentApi.post('api/report-review/summarize', data),
  deleteChat: chatId => agentApi.delete(`api/chats/${chatId}`)
};

export const useCreateChat = ({ mutationOptions } = {}) => {
  return useMutation(
    data =>
      agentService.createChat({
        ...data,
        workflow_id: authStore.workflowId || null
      }),
    mutationOptions
  );
};

export const useGetChats = ({ queryOptions, searchQuery, workflowId } = {}) => {
  return useQuery(
    [QUERY_KEYS.GET_AGENT_CHATS, { workflowId, searchQuery }],
    () => agentService.getChats(searchQuery, workflowId),
    queryOptions
  );
};

export const useGetMessagesByChatId = ({ chatId, queryOptions } = {}) => {
  return useQuery(
    [QUERY_KEYS.GET_AGENT_CHAT_MESSAGES, chatId],
    () => agentService.getMessagesByChatId(chatId),
    queryOptions
  );
};

export const useDeleteChat = ({ mutationOptions } = {}) => {
  return useMutation(chatId => agentService.deleteChat(chatId), mutationOptions);
};

export const useGetAgentInfo = ({ queryOptions } = {}) => {
  return useQuery(QUERY_KEYS.GET_AGENT_INFO, () => agentService.getInfo(), {
    staleTime: Infinity,
    ...queryOptions
  });
};

export const useSendMessageToChat = ({ mutationOptions } = {}) => {
  return useMutation(
    ({ chatId, data }) =>
      agentService.sendMessageToChat({
        chatId,
        data: getMessagePayload(data)
      }),
    mutationOptions
  );
};

export const useExplainReportReviewFinding = ({ mutationOptions } = {}) => {
  return useMutation(data => agentService.explainReportReviewFinding(data), mutationOptions);
};

export const useSummarizeReportReview = ({ mutationOptions } = {}) => {
  return useMutation(data => agentService.summarizeReportReview(data), mutationOptions);
};

/**
 * Shared SSE reader — iterates over a fetch Response body and fires typed
 * callbacks for every event type the backend can emit.
 *
 * @param {Response}   response
 * @param {object}     callbacks
 * @param {function}   callbacks.onChunk       - called with each token string
 * @param {function}   [callbacks.onFinalAnswer] - called with the authoritative final text
 * @param {function}   [callbacks.onInterrupt]  - called with interrupt payload
 * @param {function}   [callbacks.onDone]       - called when stream ends cleanly
 * @param {function}   [callbacks.onError]      - called with error string
 */
const _readSSEStream = async (
  response,
  { onChunk, onFinalAnswer, onInterrupt, onProgress, onDone, onError }
) => {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  // Track whether an interrupt was received so we skip onDone — the backend
  // always sends [DONE] after the interrupt event, but calling onDone at that
  // point would overwrite the interrupt question with an empty content string.
  let interrupted = false;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const raw = line.slice(6).trim();
      if (raw === '[DONE]') {
        if (!interrupted) onDone?.();
        return;
      }
      try {
        const parsed = JSON.parse(raw);
        if (parsed.error) {
          onError?.(parsed.error);
          return;
        }
        if (parsed.type === 'tool_start') {
          onProgress?.({ tool: parsed.tool, message: parsed.message });
        } else if (parsed.type === 'interrupt') {
          interrupted = true;
          onInterrupt?.(parsed.payload);
        } else if (parsed.type === 'final_answer' && parsed.content !== undefined) {
          // Authoritative final content — may differ from streamed tokens
          // (e.g. verbatim list_agent_capabilities with action links, or
          // text with the _ACTION_REFRESH_SENTINEL appended after mutations).
          onFinalAnswer?.(parsed.content);
        } else if (parsed.type === 'token' && parsed.content !== undefined) {
          onChunk?.(parsed.content);
        } else if (parsed.type === undefined && parsed.content !== undefined) {
          // backwards-compat with old format
          onChunk?.(parsed.content);
        }
      } catch {
        // ignore malformed SSE lines
      }
    }
  }
  if (!interrupted) onDone?.();
};

/**
 * Stream an AI agent response via Server-Sent Events.
 *
 * @param {object} opts
 * @param {string}   opts.chatId           - Conversation UUID
 * @param {object}   opts.data             - { content: string }
 * @param {function} opts.onChunk          - called with each text token
 * @param {function} [opts.onInterrupt]    - called when agent needs confirmation
 * @param {function} [opts.onDone]         - called when stream ends
 * @param {function} [opts.onError]        - called with error message string
 * @returns {Promise<void>}
 */
export const streamMessageToChat = async ({
  chatId,
  data,
  signal,
  onChunk,
  onFinalAnswer,
  onInterrupt,
  onProgress,
  onDone,
  onError
}) => {
  const token = authStore.token;
  const baseUrl = import.meta.env.VITE_AGENT_API_URL;

  try {
    const activeCompanyId = localStorage.getItem('activeCompanyId');
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };
    if (activeCompanyId) {
      headers['X-Active-Company-Id'] = activeCompanyId;
    }
    const response = await fetch(`${baseUrl}/api/messages/${chatId}/stream`, {
      method: 'POST',
      headers,
      signal,
      body: JSON.stringify(getMessagePayload(data))
    });

    if (!response.ok) {
      onError?.(`HTTP ${response.status}`);
      return;
    }

    await _readSSEStream(response, {
      onChunk,
      onFinalAnswer,
      onInterrupt,
      onProgress,
      onDone,
      onError
    });
  } catch (err) {
    if (err?.name === 'AbortError') return; // user-initiated stop — silently exit
    onError?.(err?.message ?? 'Stream error');
  }
};

/**
 * Resume a paused agent stream after the user approves/denies a confirmation.
 *
 * @param {object}   opts
 * @param {string}   opts.chatId      - Conversation UUID (same as stream call)
 * @param {boolean}  opts.approved    - true = proceed, false = cancel
 * @param {function} opts.onChunk     - called with each text token
 * @param {function} [opts.onDone]    - called when stream ends
 * @param {function} [opts.onError]   - called with error message string
 * @returns {Promise<void>}
 */
export const resumeStreamChat = async ({
  chatId,
  approved,
  onChunk,
  onFinalAnswer,
  onDone,
  onError
}) => {
  const token = authStore.token;
  const baseUrl = import.meta.env.VITE_AGENT_API_URL;

  try {
    const activeCompanyId = localStorage.getItem('activeCompanyId');
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    };
    if (activeCompanyId) {
      headers['X-Active-Company-Id'] = activeCompanyId;
    }
    const response = await fetch(`${baseUrl}/api/messages/${chatId}/resume`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ approved })
    });

    if (!response.ok) {
      onError?.(`HTTP ${response.status}`);
      return;
    }

    await _readSSEStream(response, { onChunk, onFinalAnswer, onDone, onError });
  } catch (err) {
    onError?.(err?.message ?? 'Resume error');
  }
};

/**
 * Send an audio blob to the agent backend for transcription.
 * @param {Blob} audioBlob - recorded audio
 * @returns {Promise<string>} transcribed text
 */
export const transcribeAudio = async audioBlob => {
  const baseUrl = import.meta.env.VITE_AGENT_API_URL;
  const token = authStore.token;

  const formData = new FormData();
  formData.append('file', audioBlob, 'recording.webm');

  const response = await fetch(`${baseUrl}/api/transcribe`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData
  });

  if (!response.ok) throw new Error(`Transcription failed: ${response.status}`);
  const data = await response.json();
  return data.text ?? '';
};

/**
 * Upload files (PDF, DOCX, TXT) or pasted text as personal agent knowledge.
 * Knowledge is scoped to the authenticated user — other users cannot retrieve it.
 *
 * @param {object} opts
 * @param {File[]}  [opts.files]  - array of File objects
 * @param {string}  [opts.text]   - raw pasted text
 * @param {string}  [opts.title]  - optional label for this knowledge
 * @returns {Promise<{ chunks_ingested: number }>}
 */
export const listKnowledge = () => agentApi.get('api/knowledge/list');

export const deleteKnowledge = ({ source, title }) =>
  agentApi.delete('api/knowledge/item', { params: { source, title } });

export const uploadKnowledge = async ({ files = [], text = '', title = '' }) => {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));
  formData.append('text', text);
  formData.append('title', title);
  return agentApi.post('api/knowledge/ingest', formData);
};

/**
 * Upload a file as a session attachment for a specific chat.
 * The extracted text is stored server-side and injected into the
 * agent context for all subsequent messages in this conversation.
 */
export const uploadSessionFile = async (chatId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return agentApi.post(`api/messages/${chatId}/session-files`, formData);
};

export const listSessionFiles = chatId => agentApi.get(`api/messages/${chatId}/session-files`);

export const deleteSessionFile = (chatId, filename) =>
  agentApi.delete(`api/messages/${chatId}/session-files/${encodeURIComponent(filename)}`);

export default agentService;
